import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AsyncActionStatus } from '../../actions/common.actionTypes';
import { routes } from '../../constants';
import { getMeStatus, selectIsAuthenticated, selectUser } from '../../reducers/user.selectors';
import useRouter from '../../utils/useRouter';

interface RedirectIfLoggedInProps {
  basisUserRedirectTo?: string;
  // SM-392: Zuora Basis users hitting a direct Stripe payment link (e.g.
  // `/payment?interval=...&plan=...`) must see it load, not get bounced to
  // `basisUserRedirectTo` (which also silently drops those query params).
  // Course users, Stripe Basis users (out of scope), and every other
  // authenticated cohort are unaffected.
  skipRedirectForBasisUser?: boolean;
  children?: React.ReactNode;
}

const RedirectIfLoggedIn: React.FC<RedirectIfLoggedInProps> = (props) => {
  const { basisUserRedirectTo, skipRedirectForBasisUser } = props;
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userStatus = useSelector(getMeStatus);
  const user = useSelector(selectUser);
  const { router } = useRouter();

  const redirectTarget = useMemo(() => {
    if (!isAuthenticated || !user) {
      return undefined;
    }

    if (user.isBasisUser && !user.isStripeUser && skipRedirectForBasisUser) {
      return undefined;
    }

    if (basisUserRedirectTo && (user.isBasisUser || user.isCourseUser)) {
      return basisUserRedirectTo;
    }

    if (user.isAiOnlyUser) {
      return `/${routes.aiTools.root}`;
    }

    return `/${routes.dashboard.root}`;
  }, [basisUserRedirectTo, isAuthenticated, skipRedirectForBasisUser, user]);

  useEffect(() => {
    if (redirectTarget) {
      router.replace(redirectTarget);
    }
  }, [redirectTarget, router]);

  if (isAuthenticated) {
    // Blank while we don't yet know the user's type, to avoid flashing the
    // wrong content before a pending redirect fires. ERROR is a terminal
    // state too (FETCH_ME.ERROR never transitions to READY) — treating it
    // as still-loading would blank the page forever instead of falling
    // through to the redirectTarget check below.
    const userStatusUnresolved = userStatus !== AsyncActionStatus.READY && userStatus !== AsyncActionStatus.ERROR;
    if (basisUserRedirectTo && userStatusUnresolved) {
      return <></>;
    }
    if (redirectTarget) {
      return <></>;
    }
  }

  return (
    <>
      {props.children}
    </>
  );
};

export default RedirectIfLoggedIn;
